import React, { useState, useMemo } from 'react';
import { Typography, Box, Grid2 as Grid, Pagination, Paper } from '@mui/material';
import { Chat as ChatIcon } from '@mui/icons-material';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { get } from 'lodash';
import { useQueryClient } from 'react-query';

import { moment } from '../../../I18n';
import CommentsList from './List';
import { PostStatuses, getCategoryForPostType } from '../../../Utils/Types';
import { updateQueryParams, getQueryParams } from '../../../Utils/Url';
import {
  postParticipationStarted,
  postIsClosedForParticipation,
  postHasParent,
} from '../../../Utils/Post';
import EmptyState from '../../DesignSystem/EmptyState';
import CustomIcon from '../../DesignSystem/CustomIcon';
import { Icons } from '../../../Themes';
import SortBy from './SortBy';
import useWidth from '../../../Hooks/useWidth';
import InlineCommentForm from '../../InlineCommentForm';
import useApiQuery from '../../../Hooks/useApiQuery';
import queriesConfig from '../../../Services/queriesConfig';
import usePostQuery from '../../../Hooks/usePostQuery';
import { Post } from '../../../Utils/Types/Posts';
import useCurrentUser from '../../../Hooks/useCurrentUser';

const sortingFields = [
  {
    value: '-created_at',
    label: 'RECENT',
  },
  {
    value: '-popularity',
    label: 'POPULAR',
  },
];

function CommentsSection({ post, disabled = false }: { post: Post; disabled?: boolean }) {
  const isMobile = useWidth('down', 'md');
  const intl = useIntl();
  const queryClient = useQueryClient();
  const location = useLocation();
  const { isLoggedIn } = useCurrentUser();

  const { post: parentPost } = usePostQuery(post.parent_id);
  const postCategory = getCategoryForPostType(post.type);

  const listParamsFromQueryParams = getQueryParams(location)?.comments as any;
  const [listParams, setListParams] = useState({
    page: parseInt(listParamsFromQueryParams?.page, 10) || 1,
    sortBy: listParamsFromQueryParams?.sortBy || sortingFields[0].value,
  });

  const queryParams = {
    sort: listParams.sortBy,
    page: listParams.page - 1,
  };

  const {
    data: comments,
    paging,
    isLoading,
    refetch,
  } = useApiQuery({ query: queriesConfig.getComments(post.id, queryParams) });

  const handleChangePage = (_e: any, value: any) => {
    const updatedState = { ...listParams, page: value };

    setListParams(updatedState);
    updateQueryParams({ comments: updatedState });
  };

  const handleChangeSortBy = (value: any) => {
    const updatedState = { ...listParams, sortBy: value };

    setListParams(updatedState);
    updateQueryParams({ comments: updatedState });
  };

  const emptyComments = useMemo(() => {
    const postWithParent = postHasParent(post);

    let formattedDate = moment(post.published_at).format(
      intl.formatMessage({ id: 'MOMENT.DATE.DEFAULT' }),
    );
    let message = intl.formatMessage(
      {
        id: `COMMENTS.SHOW.BE_THE_FIRST_TO_COMMENT_${postCategory.toUpperCase()}`,
      },
      { date: formattedDate },
    );
    let messageIcon = <ChatIcon style={{ fontSize: 32 }} color="primary" />;

    if (post.status === PostStatuses.DONE) {
      // when the post has a stutus 'done'
      message = intl.formatMessage({
        id: 'COMMENTS.SHOW.CONTRIBUTION_ENDED',
      });

      messageIcon = <CustomIcon src={Icons.posts.phases.coming} color="primary" size={30} />;
    } else if (postWithParent && postIsClosedForParticipation(post)) {
      // when the parent of the post has ended
      formattedDate = moment(post.included.source.end_at).format(
        intl.formatMessage({ id: 'MOMENT.DATE.DEFAULT' }),
      );

      message = intl.formatMessage(
        {
          id: 'COMMENTS.SHOW.CONTRIBUTION_ENDED_ON',
        },
        { date: formattedDate },
      );

      messageIcon = <CustomIcon src={Icons.posts.phases.coming} color="primary" size={30} />;
    } else if (postWithParent && !postParticipationStarted(post)) {
      // if the participation hasn't started yet
      formattedDate = moment(post.included.source.start_at).format(
        intl.formatMessage({ id: 'MOMENT.DATE.DEFAULT' }),
      );

      message = intl.formatMessage(
        {
          id: 'COMMENTS.SHOW.CONTRIBUTION_HAS_NOT_STARTED',
        },
        { date: formattedDate },
      );

      messageIcon = <CustomIcon src={Icons.posts.phases.coming} color="primary" size={30} />;
    }

    return (
      <EmptyState
        icon={messageIcon}
        content={
          <Typography variant="body1" data-testid="empty-state-content">
            {message}
          </Typography>
        }
      />
    );
  }, [post]);

  return (
    <>
      <Paper elevation={1}>
        <Box sx={{ p: 2 }}>
          <InlineCommentForm
            post={post}
            onSubmit={() => {
              refetch();
              queryClient.invalidateQueries(queriesConfig.getPost(post.id).queryKey);
            }}
            isLoggedIn={isLoggedIn}
            parentPost={parentPost}
          />
        </Box>
      </Paper>
      <Box sx={{ my: 3 }}>
        <Grid container sx={{ alignItems: 'center' }}>
          <Grid size={{ xs: 12, sm: 'grow' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexDirection: isMobile ? 'column' : 'row',
              }}
            >
              <Box sx={{ my: { xs: 3, md: 0 } }}>
                <Typography variant="body1" component="div" sx={{ fontWeight: 'bold' }}>
                  <FormattedMessage
                    id={`COMMENTS.SHOW.COUNT_${postCategory.toUpperCase()}`}
                    values={{ count: post.post_contributions_stats.comments_count }}
                  />
                </Typography>
              </Box>

              <SortBy
                value={listParams.sortBy}
                items={sortingFields}
                onChange={handleChangeSortBy}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
      {!post.post_contributions_stats.comments_count && !comments?.length ? (
        emptyComments
      ) : (
        <Box>
          <CommentsList
            comments={comments || []}
            loading={isLoading}
            post={post}
            postAuthorId={get(post, 'included.author.id')}
            disabled={disabled}
            refetch={refetch}
            parentPost={parentPost}
          />

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Pagination
              count={paging.total_pages}
              page={listParams.page}
              onChange={handleChangePage}
              color="primary"
              variant="outlined"
            />
          </Box>
        </Box>
      )}
    </>
  );
}

export default CommentsSection;
